import { getReservationRemainingMs, useReservationCountdown } from 'noddi-util';
import { useEffect, useRef } from 'react';
import type { StationarySlotHold } from './useReserveStationarySlot';
import { useCancelStationaryHold } from './useReserveStationarySlot';

/** Client-side TTL check for a hold (no `reservedUntil` counts as expired). */
export const isStationaryHoldExpired = (hold: StationarySlotHold | null | undefined): boolean =>
  hold != null && (getReservationRemainingMs(hold.reservedUntil) ?? 0) <= 0;

/**
 * Single owner for stationary hold TTL expiry:
 * best-effort cancel the bay, keep credentials in the store for re-reserve.
 */
export function useCancelHoldOnExpiry(hold: StationarySlotHold | null | undefined): boolean {
  const { cancelHold } = useCancelStationaryHold();
  const holdRef = useRef(hold);

  useEffect(() => {
    holdRef.current = hold;
  }, [hold]);

  useReservationCountdown(hold?.reservedUntil, {
    onExpired: () => {
      const current = holdRef.current;
      if (current) {
        void cancelHold(current);
      }
    }
  });

  return isStationaryHoldExpired(hold);
}

/**
 * Best-effort cancel when a store hold goes non-null → null
 * (garage change, draft reset, clear). Mount once at the flow root.
 *
 * `subscribe` should call the listener with (current, previous) hold values.
 */
export function useReleaseStationaryHoldOnClear(
  subscribe: (
    listener: (current: StationarySlotHold | null | undefined, previous: StationarySlotHold | null | undefined) => void
  ) => () => void
) {
  const { cancelHold } = useCancelStationaryHold();
  const cancelRef = useRef(cancelHold);

  useEffect(() => {
    cancelRef.current = cancelHold;
  }, [cancelHold]);

  useEffect(
    () =>
      subscribe((current, previous) => {
        if (previous && !current) {
          void cancelRef.current(previous);
        }
      }),
    [subscribe]
  );
}
